import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {LandingService} from './landing.service';
import {ActivatedRoute, Router} from '@angular/router';
import { MetaService } from '../shared/services/meta.service';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { Subject} from 'rxjs';
import {PrerenderService} from "../../services/prerender.service";
import { CmsService } from '../cms/services/cms.service';
import { NetworkConfigs } from '../shared/models/networks.enum';
import { DOCUMENT } from '@angular/common';
import { ProjectService } from '../shared/services/project.service';

@Component({
  selector: 'landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss']
})
export class LandingComponent implements OnInit, OnDestroy {
  private _destroy$: Subject<boolean> = new Subject();
  public isLoaded = false;
  public params: {
    network: number | string,
    agencyId: string,
    pageType: string,
    contentId: number | string,
    pageId: string
  };
  public catalogParams: {
    page: number | string;
    section: number | string;
  }

  private _staticHost: string;
  public host: string;

  private holidayParams = ['struttura', 'top', 'data', 'sol', 'source'];
  private cruiseParams = ['itinerario', 'compagnia', 'nave', 'porto', 'durata', 'da', 'a', 'volo', 'source'];

  public holidaySegments = { struttura: null, top: null, data: null, sol: null, source: null };
  public cruiseSegments = { itinerario: null, compagnia: null, nave: null, porto: null, durata: null, da: null, a: null, volo: null, source: null };

  public agencyInfo;

  private isPrerender: boolean;
  private prerenderTimeout;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private _router: Router,
    private _route: ActivatedRoute,
    private _landingService: LandingService,
    private _metaService: MetaService,
    private _prerenderService: PrerenderService,
    private _cmsService: CmsService,
    private _projectService: ProjectService,
  ) {}

  ngOnInit(): void {
    const isLandingSocial = window.location.pathname.toLowerCase().includes("/landing/social");
    const queryParams = this._route.snapshot.queryParams;
    const segments = this._route.snapshot['_urlSegment'].segments.map(x => x.path);

    this.isPrerender = this._prerenderService.isPrerender;

    try {
      if (isLandingSocial) {
          this.handleSocialPath(queryParams, segments);
        // TEST LANDING
        // SITE https://localhost:4350/landing/social/card/6/46/1025?p=index&network=46&code=1025
        // NO SITE https://localhost:4350/landing/social/card/6/46/1025?p=index&network=46&code=104
      } else {
        this.handleAllPaths(queryParams, segments);
      }
    } catch (e) {
      this._prerenderService.setPrerenderLoadingMeta(true);
      throw new Error(e);
    }
  }

  ngOnDestroy(): void {
    if (this.prerenderTimeout) clearTimeout(this.prerenderTimeout);
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  handleSocialPath(queryParams, segments) {
    this.getCardAndSetOG(segments).pipe(
      takeUntil(this._destroy$),
      catchError(e => {
        this._prerenderService.setPrerenderLoadingMeta(true);
        throw new Error(e);
      })
    ).subscribe(result => {
      const newSegments = result.segments;
      const agencyInfo = result.agency;
      const agencyCode = result.agencyCode;
      const networkCode = result.networkCode;
      const isSocialCard = result.isSocialCard;
      const pageType = result.type;
      const pagePath = result.path;

      // https://localhost:4350/landing/social/card/44/46/1025?network=46&code=1025 not static
      // https://localhost:4350/landing/social/card/54/46/1025?network=46&code=1025 static

      this.params = {
        network: networkCode,
        agencyId: agencyCode,
        pageType: newSegments[1].toLowerCase(),
        contentId: newSegments[3] ?? null,
        pageId: newSegments[2]
      }

      if (this.isPrerender) {
        return;
      }
      this.setAgencyInfo(agencyInfo, true);
      if(isSocialCard) {
        this.redirectToCustomLanding({}, true);
        return;
      }

      if (this.params.pageType == 'cataloghi') {
        this.createCatalogLinkAndRedirect(newSegments);
        return;
      }

      if (pageType) {
        let page = {
          id: this.params.contentId,
          path: pagePath
        }
        // TEST https://localhost:4350/landing/social/card/13/46/GEO1025/?p=index&network=46&code=1025
        // TEST NO SITE https://localhost:4350/landing/social/card/13/46/104/?p=index&network=46&code=104
        if (pageType == 'news/') this.redirectToWebsiteContentLanding("news", page);
        if (pageType == 'offerte/') this.redirectToWebsiteContentLanding("offerte", page);
        if (pageType == 'le-nostre-proposte/') this.redirectToWebsiteContentLanding("le-nostre-proposte", page);
        return;
      }

      this.setAllParams(queryParams, newSegments).then(
        (isLinkReady) => {
          this.handleRedirects(agencyInfo, isLinkReady);
        }
      );
    });
  }

  handleAllPaths(queryParams, segments) {
    this.params = {
      network: this.getNetworkId(queryParams.network),
      agencyId: queryParams.code,
      pageType: segments[1].toLowerCase(),
      contentId: segments[3],
      pageId: segments[2]
    }
    this.setAllParams(queryParams, segments).then(
      (isLinkReady) => {
        this._landingService.getAgencyInfo(this.params.agencyId, this.params.network).then(
          (res: any) => {
            // al landing se è wtg comunque setto lo style e il brand config
            if(res?.networkOwner === 16) this.forceWtgStyle();
            this.handleRedirects(res, isLinkReady);
          },
          (error) => {
            // TODO ERROR
            console.log('ERROR, NO PATH', error);
          }
        );
      },
      (error) => {
        console.log('Error', error);
      }
    );
  }

  // Metodo ricopiato dall'appcomponent
  forceWtgStyle(): void {
    const networkConfig = NetworkConfigs[16];
    this._projectService.network = "WTG";
    this._cmsService.brandConfig$.next(networkConfig);
    let bodyEl = this.document.getElementById("body-el");
    bodyEl.classList.add(networkConfig.bodyClass);
  }

  //#region helpers - OK
  handleRedirects(agencyInfo, isLinkReady) {
    const isNoSiteAgency = window.location.hostname.includes('lamiagenziaviaggi');
    if (!agencyInfo) {
      console.log('ERROR, INCORRECT AGENCY DETAILS');
      // WpORKAROUND
      if (this.params.pageType == 'cataloghi') {
        this.host = this.getHost(Number(this.params.network), isNoSiteAgency);
        this.redirectToCatalogsLanding(true);
      }
    } else {
      this.setAgencyInfo(agencyInfo, isNoSiteAgency);
      // REDIRECT MAIN LOGIC, after all parameters are set and mapped
      if (this.params.pageType == 'custom') {
        this.getCustomContentAndRedirectToLanding(Number(this.params.contentId)); // OK
      } else if (this.params.pageType == 'vacanze' || this.params.pageType == 'crociere') {
        this.redirectToDetailLanding(isLinkReady); // OK
      } else if (this.params.pageType == 'cataloghi') {
        this.redirectToCatalogsLanding();
      } else if (this.params.pageType == 'news') {
        this.redirectToWebsiteContentLanding('news');
      } else if (this.params.pageType == 'destinazione' || this.params.pageType == 'offerte') {
        this.redirectToWebsiteContentLanding('offerte');
      } else if (this.params.pageType == 'le-nostre-proposte') {
        this.redirectToWebsiteContentLanding('le-nostre-proposte');
      } else {
        console.error(`TYPE ${this.params.pageType} DOES NOT MATCH WITH ANY ALLOWED ONE`);
      }
    }
  }

  private setAgencyInfo(agencyInfo, isLandingFromSocial: boolean = false) {
    this.agencyInfo = agencyInfo;
    this._staticHost = this.getHost(Number(this.params.network), isLandingFromSocial);
    if (!this.isPrerender) this.setFavicon(Number(this.params.network));
    this.host = this.agencyInfo.isWebsite ? this.createHostURI(this.agencyInfo.sito) : this._staticHost;
    this._landingService.agencyInfo$.next(this.agencyInfo);
    // DEV, comment out before deploy
    //this.host = 'https://localhost:4350';
  }

  private setFavicon(network: number) {
    if (network == 12 || network == 16) {
      document.querySelector('#custom-favicon').setAttribute('href', 'favicon_wtg.ico');
    } else {
      document.querySelector('#custom-favicon').setAttribute('href', 'favicon_geo.ico');
    }
  }

  private getCardAndSetOG(segments) {
    const cardId = segments[3];
    const networkCode = segments[4];
    const agencyCode = this.removeAgencyCodePrefix(segments[5]);

    const agency$ = this._landingService.getAgencyByExternalId(agencyCode, networkCode);

    return this._landingService.getSocialCardById(cardId).pipe(
      switchMap(card => agency$.pipe(
        map(agency => {
          const host = agency.isWebsite ? this.createHostURI(agency.sito) : this.getHost(Number(networkCode), true);
          const productData = JSON.parse(card.productData);
          let link = productData.url ?? null;
          let type = productData.type ?? null;
          let path = productData.path ?? null;
          let imageDimensions = productData.imageDimension ?? null
          let width = imageDimensions?.width.toString() ?? null;
          let height = imageDimensions?.height.toString() ?? null;

          let queryString = `?p=index&code=${agencyCode}&network=${networkCode}`;
          let isSocialCard = false;

          if (!link) {
            isSocialCard = true;
            link = `/custom/content/${card.id}`;
            queryString = queryString.concat("&isSocialCard=true");
          }
          const fullUrl = host + link + queryString;

          this._metaService.setOGCard(
            card.title,
            agency.denominazione,
            card.description,
            card.image,
            window.location.href,
            "article",
            "it_IT",
            width,
            height
          );

          this._metaService.addMeta(
            "og:canonical", window.location.href
          )

          const metasToCheck = {
            title: card.title,
            site_name: agency.denominazione,
            description: card.description,
            image: card.image,
            url: window.location.href,
            type: "website",
            locale: "it_IT",
          }
          this._prerenderService.handleMetaLoadedAndCreateElement(metasToCheck);

          const objToReturn = {
            fullUrl: fullUrl,
            agency: agency,
            segments: link.split("/"),
            agencyCode, networkCode,
            isSocialCard, type: type, path: path
          }

          return objToReturn;
        })
      ))
    )
  }

  private setAllParams(queryParams, segments): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.params.agencyId.includes(';src')){
        const idx = this.params.agencyId.indexOf(';src');
        this.params.agencyId = this.params.agencyId.substring(0, idx);
      }
      this.catalogParams = {
        page: queryParams.page ? queryParams.page : null,
        section: queryParams.section ? queryParams.section : null
      }

      // Resolve immediately if 'custom', 'detail', 'list' or 'player', no need to go further, the link is usable by internal routes, resolve to true
      if (segments[2] == 'dettaglio' || segments[2] == 'lista' || segments[2] == 'player' || this.params.pageType == 'le-nostre-proposte' ||
        this.params.pageType == 'destinazione' || this.params.pageType == 'news' || this.params.pageType == 'offerte' || this.params.pageType == 'custom') {
        resolve(true);
      }

      if (!this.params.network) reject();
      // otherwise map legacy detail link to new ones and resolve to false, the link is not ready yet and will need to created outside
      if (this.params.pageType == 'vacanze') {
        for (let param of this.holidayParams) {
          this.holidaySegments[param] = segments[segments.indexOf(segments.find(x => x == param)) + 1];
        }
      } else if (this.params.pageType == 'crociere') {
        for (let param of this.cruiseParams) {
          this.cruiseSegments[param] = segments[segments.indexOf(segments.find(x => x == param)) + 1];
        }
      } else {
        reject();
      }
      // the parameters have been mapped but this is still a proxy landing link, not an internal usable link, resolve to false
      resolve(false);
    });
  }

  private removeAgencyCodePrefix(agencyCode): string {
    return agencyCode.replace(/^WTG|^GEO/, '');
  }

  private getHost(networkId: number, isLandigFromSocial: boolean = false): string {
    if(isLandigFromSocial){
      return 'https://lamiagenziaviaggi.it';
    } else{
      return networkId == 46 ? "https://www.geotn.it" : 'https://www.vacanzewelcometravel.it';
    }
  }

  private createHostURI(site: string): string {
    return site.includes('http') ? site: "//" + site;
  }

  private getNetworkId(networkString): number {
    if (networkString == 'Geo Travel Network' || networkString == '46' || networkString == 46) {
      return 46;
    }
    else if (networkString == 'Welcome Travel' || networkString == 'Welcome Travel Store' || networkString == 'Welcome Travel Shop' || networkString == '16' || networkString == '12' || networkString == 16) {
      return 16;
    }
    else {
      return null;
    }
  }

  private createDetailLink(): string {
    let detailLink = '';
    if (this.params.pageType == 'vacanze') {
      detailLink += `/vacanze/dettaglio`;
      detailLink += `/${this.holidaySegments.struttura}/${this.holidaySegments.top}/${this.holidaySegments.sol}/${this.holidaySegments.source}`;
      detailLink += `/struttura/newsletter/dal/${this.holidaySegments.data}/al/${this.holidaySegments.data}`

    } else if (this.params.pageType == 'crociere') {
      detailLink += '/crociere/dettaglio';
      detailLink += `/${this.cruiseSegments.source}/${this.cruiseSegments.volo}/pacchetto/newsletter/volo/f`;
      detailLink += `/dal/${this.cruiseSegments.da}/al/${this.cruiseSegments.a}`;
    }
    return detailLink;
  }

  private getCustomContentAndRedirectToLanding(contentId: number): void {
    this._landingService.getCustomContent(contentId).then(
      (customContent) => {
        if (customContent.NewsDest) {
          const type = customContent.NewsDest.type.id;
          if (type == 2) { // NEWS
            this.redirectToWebsiteContentLanding("news", customContent.NewsDest);
          } else if (type == 3) { // MULTI-PRODUCT - destination
            this.redirectToWebsiteContentLanding("offerte", customContent.NewsDest);
          }
        } else { // CUSTOM PRODUCT created in legacy module or new social tool
          this.redirectToCustomLanding(customContent);
        }
      },
      (error) => {
        console.error("GET CUSTOM CONTENT ERROR", error);
      }
    );
  }
  //#endregion

  private redirectToWebsiteContentLanding(route: string, pageObj?: {id: number | string, path: number | string}) {
    console.log(route, pageObj);
    // test site https://localhost:4350/landing/custom/content/3470?p=index&code=1025&network=Geo%20Travel%20Network
    // test no site  https://localhost:4350/landing/custom/content/3470?p=index&code=104&network=Geo%20Travel%20Network
    let page = pageObj;

    if (page == undefined) {
      page = {
        id: this.params.pageId,
        path: this.params.contentId
      }
    }

    if (this.agencyInfo.isWebsite) {
      window.location.href = this.host + '/' + route + '/' + page.path;
    } else {
      let linkPath = '/landing/' + route + '/' + page.id + '/' + page.path;
      linkPath = linkPath.replace('offerte', 'le-nostre-proposte');
      if (window.location.origin != this.host) {
        window.location.href = this.host + linkPath + window.location.search;
        console.log("REDIRECT INTERNALLY", window.location.href);
      } else {
        console.log("NAVIGATE INTERNALLY", linkPath);
        this._router.navigate([linkPath], { queryParams: {network: this.params.network, code: this.params.agencyId}});
        this.isLoaded = true;
      }
    }
  }

  private redirectToCustomLanding(customContent: any, isSocialCard?: boolean): void { // SHOULD BE OK - Needs PROD testing
    console.log("OTHER CONTENT", customContent, this.params);

    const route = isSocialCard === undefined ? '/newsletter-landing/' : '/card-landing/';
    const socialParam = isSocialCard === undefined ? '' : '&isSocialCard=true';

    // test site https://localhost:4350/landing/custom/content/3467?p=index&code=1025&network=Geo%20Travel%20Network
    // test no site https://localhost:4350/landing/custom/content/3467?p=index&code=104&network=Geo%20Travel%20Network

    if (this.agencyInfo.isWebsite) {

      window.location.href = this.host + route + this.params.contentId + `?network=${this.params.network}${socialParam}`;
    } else {
      if (window.location.origin != this.host) {
        window.location.href = this.host + window.location.pathname + window.location.search + socialParam;
      } else {
        this.isLoaded = true;
      }
    }
  }

  // For and from social tool - fast workaround
  private createCatalogLinkAndRedirect(newSegments): void {
    let linkPath = `/${newSegments[1]}/${newSegments[2]}/${newSegments[3]}`;
    if (this.agencyInfo?.isWebsite) {
      window.location.href = this.host + linkPath + `?network=${this.params.network}&code=${this.params.agencyId}`;
    } else {
      window.location.href = this.host + '/landing' + linkPath + `?network=${this.params.network}&code=${this.params.agencyId}`;
    }
  }

  private redirectToCatalogsLanding(networkRedirect?: boolean): void { // SHOULD BE OK - Needs PROD testing
    // TODO
    console.log("CATALOGS", this.params);
    // test site https://localhost:4350/landing/cataloghi/lista/?p=index&code=1025&network=Geo%20Travel%20Network
    // test site https://localhost:4350/landing/cataloghi/player/3333?p=index&code=1025&network=Geo%20Travel%20Network
    // test no site https://localhost:4350/landing/cataloghi/lista/?p=index&code=104&network=Geo%20Travel%20Network
    // test no site https://localhost:4350/landing/cataloghi/player/5555?p=index&code=104&network=Geo%20Travel%20Network
    let linkPath;
    if (this.agencyInfo?.isWebsite) {
      linkPath = window.location.pathname.substring(window.location.pathname.indexOf("landing/") + 7);
      console.log("LINKPATH", linkPath);
      window.location.href = this.host + linkPath + `?network=${this.params.network}&code=${this.params.agencyId}` +
                             `${this.catalogParams.page ? '&page=' + this.catalogParams.page : ''}` +
                             `${this.catalogParams.section ? '&section=' + this.catalogParams.page: ''}`
    } else {
      if (window.location.origin != this.host) {
        window.location.href = this.host + window.location.pathname + window.location.search;
      } else {
        if (networkRedirect != undefined) {
          const redirectPath = window.location.pathname.replace('/landing', '');
          window.location.href = this.host + redirectPath + window.location.search;
        } else {
          this._router.navigate([window.location.pathname],{ queryParams: {network: this.params.network, code: this.params.agencyId,
                                                                           page: this.catalogParams.page, section: this.catalogParams.section}});
        }
        this.isLoaded = true;
      }
    }
  }

  private redirectToDetailLanding(isLinkReady: boolean): void { // SHOULD BE OK - Needs PROD testing
    let linkPath;

    if (isLinkReady == true) { // is already a mappaed detail link
      linkPath = window.location.pathname;
    } else { // detail link is not ready, create one
      linkPath = this.createDetailLink();
    }
    const landingLinkPath = linkPath.includes('/landing') ? linkPath : '/landing' + linkPath;

    if (this.agencyInfo.isWebsite) {
      // if agency has a website with us, redirect to detail page
      if (isLinkReady) linkPath = linkPath.replace('/landing', ''); // preventive
      window.location.href = this.host + linkPath + `?network=${this.params.network}&code=${this.params.agencyId}`;
    } else { // otherwise show the network domain white label site of the agency
      // if origin is not the correct host (which is set above depending on the agency network)
      if (window.location.origin != this.host) {
        window.location.href = this.host + window.location.pathname + window.location.search;
      } else {
        // otherwise if it is already on the right domain, navigate to the detail internal route that shows the agency header and footer
        this._router.navigate([landingLinkPath], { queryParams: {network: this.params.network, code: this.params.agencyId}});
        this.isLoaded = true;
      }
    }
  }

  // PROD TESTS no site GEO
  // custom https://website.offertetouroperator.com/landing/custom/content/3467?p=index&code=104&network=Geo%20Travel%20Network
  // news https://website.offertetouroperator.com/landing/custom/content/3472?p=index&code=104&network=Geo%20Travel%20Network
  // destinazione https://website.offertetouroperator.com/landing/custom/content/3470?p=index&code=104&network=Geo%20Travel%20Network
  // cruise https://website.offertetouroperator.com/landing/crociere/itinerario/5/compagnia/132/nave/210/porto/2000069/durata/7/da/15-06-2023/a/15-06-2023/volo/0/source/PMO07A09?p=index&code=104&network=Geo%20Travel%20Network
  // holiday https://website.offertetouroperator.com/landing/vacanze/struttura/37159/top/1016058/data/11-06-2023/sol/378156A4-20230611-3677-50728-8-7/source/52?p=index&code=104&network=Geo%20Travel%20Network

  // PROD TESTS  site WTG
  // custom https://website.offertetouroperator.com/landing/custom/content/3467?p=index&code=1345&network=Welcome%20Travel
  // news https://website.offertetouroperator.com/landing/custom/content/3472?p=index&code=1345&network=Welcome%20Travel
  // destinazione https://website.offertetouroperator.com/landing/custom/content/3470?p=index&code=1345&network=Welcome%20Travel
  // cruise https://website.offertetouroperator.com/landing/crociere/itinerario/5/compagnia/132/nave/210/porto/2000069/durata/7/da/15-06-2023/a/15-06-2023/volo/0/source/PMO07A09?p=index&code=1345&network=Welcome%20Travel
  // holiday https://website.offertetouroperator.com/landing/vacanze/struttura/37159/top/1016058/data/11-06-2023/sol/378156A4-20230611-3677-50728-8-7/source/52?p=index&code=1345&network=Welcome%20Travel

  // PROD TESTS  widget WTG / no site WTG
  // custom https://website.offertetouroperator.com/landing/custom/content/3467?p=index&code=W0534&network=Welcome%20Travel
  // news https://website.offertetouroperator.com/landing/custom/content/3472?p=index&code=W0534&network=Welcome%20Travel
  // destinazione https://website.offertetouroperator.com/landing/custom/content/3470?p=index&code=W0534&network=Welcome%20Travel
  // cruise https://website.offertetouroperator.com/landing/crociere/itinerario/5/compagnia/132/nave/210/porto/2000069/durata/7/da/15-06-2023/a/15-06-2023/volo/0/source/PMO07A09?p=index&code=W0534&network=Welcome%20Travel
  // holiday https://website.offertetouroperator.com/landing/vacanze/struttura/37159/top/1016058/data/11-06-2023/sol/378156A4-20230611-3677-50728-8-7/source/52?p=index&code=W0534&network=Welcome%20Travel


}
